import React, { useEffect, useState } from 'react';
import api from '../services/api';
import FilterableTable from '../components/FilterableTable';
import Toast from '../components/Toast';
import Combobox from '../components/Combobox';
import { filtreazaCantitate, normalizeazaNumar, formatCantitate } from '../utils/format';
import { formatNumarRaport, exportaCSV, printeazaTabel } from '../utils/export';

export default function Stoc() {
  const [stoc, setStoc] = useState([]);
  const [extinse, setExtinse] = useState(new Set()); // magazieId-uri deschise
  const [loading, setLoading] = useState(true);
  const [consumModal, setConsumModal] = useState(null); // randul de stoc pentru care se da in consum
  const [cantitateConsum, setCantitateConsum] = useState('');
  const [motivConsum, setMotivConsum] = useState('');
  const [documentConsum, setDocumentConsum] = useState('');
  const [dataDocumentConsum, setDataDocumentConsum] = useState('');
  const [produsFinitTintaId, setProdusFinitTintaId] = useState('');
  const [lotProductieId, setLotProductieId] = useState('');
  const [loturiActiveProdus, setLoturiActiveProdus] = useState([]);
  // Produsele cu cel putin un lot activ (in productie) - doar acestea au sens de
  // ales cand dai un material "in consum catre productie" din Stoc curent.
  const [produseInProductie, setProduseInProductie] = useState([]);
  useEffect(() => {
    if (motivConsum !== 'PRODUCTIE') return;
    api.get('/api/loturi/active').then(res => {
      const unice = new Map();
      res.data.forEach(l => unice.set(l.produsFinitId, { id: l.produsFinitId, cod: l.cod, denumire: l.denumire }));
      setProduseInProductie([...unice.values()]);
    });
  }, [motivConsum]);
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Loturile de productie active (in lucru) pentru produsul finit tinta ales -
  // fiecare bucata dintr-o comanda e un lot separat, deci alegerea lotului exact
  // nu se poate deduce automat daca sunt mai multe simultan (bucati/comenzi diferite).
  useEffect(() => {
    if (motivConsum !== 'PRODUCTIE' || !produsFinitTintaId) {
      setLoturiActiveProdus([]);
      return;
    }
    api.get(`/api/loturi/active-pentru-produs?produsFinitId=${produsFinitTintaId}`)
      .then(res => setLoturiActiveProdus(res.data));
  }, [motivConsum, produsFinitTintaId]);

  function incarcaStoc() {
    setLoading(true);
    return api.get('/api/stoc?doarMateriale=true').then(res => setStoc(res.data)).finally(() => setLoading(false));
  }

  useEffect(() => { incarcaStoc(); }, []);

  function deschideConsum(s) {
    setConsumModal(s);
    setCantitateConsum('');
    setMotivConsum('');
    setDocumentConsum('');
    setDataDocumentConsum(new Date().toISOString().slice(0, 10));
    setProdusFinitTintaId('');
    setLotProductieId('');
  }

  async function confirmaConsum(e) {
    e.preventDefault();
    if (submitting) return;
    if (!motivConsum) {
      setToast({ message: 'Alege motivul iesirii', type: 'error' });
      return;
    }
    if (motivConsum === 'PRODUCTIE' && !produsFinitTintaId) {
      setToast({ message: 'Alege produsul finit pentru care se consuma materialul', type: 'error' });
      return;
    }
    if (motivConsum === 'PRODUCTIE' && !lotProductieId) {
      setToast({ message: loturiActiveProdus.length > 0
        ? 'Alege lotul de productie (bucata) pentru care se consuma acest material'
        : 'Nu exista niciun lot de productie activ pentru acest produs - porneste unul din Comenzi', type: 'error' });
      return;
    }
    if (!dataDocumentConsum) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    const cantitateNormalizata = normalizeazaNumar(cantitateConsum);
    const cant = Number(cantitateNormalizata);
    if (!cant || cant <= 0) {
      setToast({ message: 'Introdu o cantitate valida', type: 'error' });
      return;
    }
    if (cant > Number(consumModal.cantitate)) {
      setToast({ message: `Cantitatea depaseste stocul disponibil (${formatCantitate(consumModal.cantitate)} ${consumModal.um})`, type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/miscari/iesire', {
        itemTip: consumModal.itemTip, itemId: consumModal.itemId,
        magazieId: consumModal.magazieId, cantitate: cantitateNormalizata,
        motiv: motivConsum, lotProductieId: motivConsum === 'PRODUCTIE' ? lotProductieId : null,
        document: documentConsum || null, dataDocument: dataDocumentConsum,
        observatii: 'Dat in consum din Stoc curent'
      });
      setToast({ message: 'Cantitate data in consum', type: 'ok' });
      setConsumModal(null);
      incarcaStoc(); // stocul se actualizeaza automat pe ecran, fara reload manual
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la inregistrarea consumului', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const coloane = [
    { key: 'cod', label: 'Cod', render: s => s.cod || '-' },
    { key: 'denumire', label: 'Denumire' },
    { key: 'cantitate', label: 'Cantitate', render: s => `${formatCantitate(s.cantitate)} ${s.um}` },
    { key: 'pret', label: 'Pret', render: s => s.pret != null ? `${formatCantitate(s.pret)} lei` : '-' },
    { key: 'valoare', label: 'Valoare', render: s => s.valoare != null ? `${formatCantitate(s.valoare)} lei` : '-' },
    { key: 'status', label: 'Status', filterable: false, render: s => {
      if (!(s.pragAlerta > 0)) return <span className="badge" style={{ background: '#e2e8f0', color: '#334155' }}>Fara prag</span>;
      const subPrag = s.cantitate < s.pragAlerta;
      return subPrag ? <span className="badge alerta">Sub prag</span> : <span className="badge ok">OK</span>;
    } },
    { key: 'descarcare', label: 'Descarcare', filterable: false, render: s => (
      <button type="button" className="btn-edit" onClick={() => deschideConsum(s)}>Dati in consum</button>
    ) }
  ];

  const EXPORT_COLOANE = [
    { key: 'cod', label: 'Cod' },
    { key: 'denumire', label: 'Denumire' },
    { key: 'cantitate', label: 'Cantitate' },
    { key: 'um', label: 'UM' },
    { key: 'pret', label: 'Pret' },
    { key: 'valoare', label: 'Valoare' }
  ];

  function exportaMagazie(g) {
    const randuri = g.randuri.map(s => ({
      cod: s.cod || '', denumire: s.denumire || '', cantitate: formatNumarRaport(s.cantitate),
      um: s.um || '', pret: formatNumarRaport(s.pret), valoare: formatNumarRaport(s.valoare)
    }));
    exportaCSV(`stoc-${g.magazieNume}.csv`, EXPORT_COLOANE, randuri);
  }

  function tipareseMagazie(g) {
    const coloane = [
      { key: 'cod', label: 'Cod' },
      { key: 'denumire', label: 'Denumire' },
      { key: 'cantitate', label: 'Cantitate', dreapta: true },
      { key: 'pret', label: 'Pret', dreapta: true },
      { key: 'valoare', label: 'Valoare', dreapta: true }
    ];
    const randuri = g.randuri.map(s => ({
      cod: s.cod || '', denumire: s.denumire || '',
      cantitate: `${formatNumarRaport(s.cantitate)} ${s.um || ''}`,
      pret: s.pret != null ? `${formatNumarRaport(s.pret)} lei` : '-',
      valoare: s.valoare != null ? `${formatNumarRaport(s.valoare)} lei` : '-'
    }));
    printeazaTabel(`Stoc curent - ${g.magazieNume}`, coloane, randuri);
  }

  // Grupare pe magazie (client-side - /api/stoc intoarce deja magazieNume pe
  // fiecare rand), cu totalul de valoare calculat per magazie si general.
  const grupuriMagazii = [];
  {
    const dupaMagazie = new Map();
    for (const s of stoc) {
      const cheie = s.magazieId;
      if (!dupaMagazie.has(cheie)) {
        dupaMagazie.set(cheie, { magazieId: cheie, magazieCod: s.magazieCod, magazieNume: s.magazieNume, randuri: [], total: 0 });
      }
      const g = dupaMagazie.get(cheie);
      g.randuri.push(s);
      g.total += Number(s.valoare) || 0;
    }
    grupuriMagazii.push(...[...dupaMagazie.values()].sort((a, b) => (a.magazieNume || '').localeCompare(b.magazieNume || '')));
  }
  const totalGeneral = grupuriMagazii.reduce((sum, g) => sum + g.total, 0);

  function toggleGrup(magazieId) {
    setExtinse(prev => {
      const nou = new Set(prev);
      if (nou.has(magazieId)) nou.delete(magazieId); else nou.add(magazieId);
      return nou;
    });
  }

  const toateChile = grupuriMagazii.map(g => g.magazieId);
  const totExtins = toateChile.length > 0 && toateChile.every(c => extinse.has(c));

  function exportaTot() {
    const randuri = stoc.map(s => ({
      cod: s.cod || '', denumire: s.denumire || '', cantitate: formatNumarRaport(s.cantitate),
      um: s.um || '', pret: formatNumarRaport(s.pret), valoare: formatNumarRaport(s.valoare)
    }));
    exportaCSV('stoc-curent-total.csv', EXPORT_COLOANE, randuri);
  }

  function tipareseTot() {
    const coloane = [
      { key: 'cod', label: 'Cod' },
      { key: 'denumire', label: 'Denumire' },
      { key: 'cantitate', label: 'Cantitate', dreapta: true },
      { key: 'pret', label: 'Pret', dreapta: true },
      { key: 'valoare', label: 'Valoare', dreapta: true }
    ];
    const randuri = stoc.map(s => ({
      cod: s.cod || '', denumire: s.denumire || '',
      cantitate: `${formatNumarRaport(s.cantitate)} ${s.um || ''}`,
      pret: s.pret != null ? `${formatNumarRaport(s.pret)} lei` : '-',
      valoare: s.valoare != null ? `${formatNumarRaport(s.valoare)} lei` : '-'
    }));
    printeazaTabel('Stoc curent - total', coloane, randuri);
  }

  function toggleToate() {
    setExtinse(totExtins ? new Set() : new Set(toateChile));
  }

  return (
    <div>
      <h1>Stoc curent</h1>
      <p style={{ fontSize: 14, color: '#444', marginTop: -8 }}>
        Total general: <strong>{formatCantitate(totalGeneral)} lei</strong>
      </p>
      <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn secondary" onClick={toggleToate}>
          {totExtins ? 'Restrange tot' : 'Extinde tot'}
        </button>
        <button type="button" className="btn-csv" onClick={exportaTot}>Export CSV (total)</button>
        <button type="button" className="btn-print" onClick={tipareseTot}>Print (total)</button>
      </div>

      {loading ? <p>Se incarca...</p> : (
        <div className="card">
          {grupuriMagazii.map(g => (
            <div key={g.magazieId} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', borderBottom: '1px solid #eee' }}>
                <div onClick={() => toggleGrup(g.magazieId)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <span>{extinse.has(g.magazieId) ? '\u25be' : '\u25b8'}</span>
                  <span style={{ fontWeight: 600 }}>{g.magazieCod ? `${g.magazieCod} - ` : ''}{g.magazieNume}</span>
                  <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>({g.randuri.length} articole)</span>
                  <span style={{ fontWeight: 600, color: '#128a3e', fontSize: 13 }}>Total: {formatCantitate(g.total)} lei</span>
                </div>
                <button type="button" className="btn-csv" onClick={() => exportaMagazie(g)}>Export CSV</button>
                <button type="button" className="btn-print" onClick={() => tipareseMagazie(g)}>Print</button>
              </div>
              {extinse.has(g.magazieId) && (
                <FilterableTable columns={coloane} rows={g.randuri.map((s, i) => ({ ...s, id: i }))}
                  rowClassName={s => (s.pragAlerta > 0 && s.cantitate < s.pragAlerta) ? 'alerta' : ''} />
              )}
            </div>
          ))}
          {grupuriMagazii.length === 0 && <p>Niciun stoc inregistrat inca.</p>}
        </div>
      )}

      {consumModal && (
        <div className="modal-overlay" onClick={() => setConsumModal(null)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dati in consum</h2>
              <button type="button" className="modal-close" onClick={() => setConsumModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaConsum}>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: '#888', marginTop: 0 }}>
                  {consumModal.denumire} &middot; {consumModal.magazieNume} &middot; Stoc disponibil: {formatCantitate(consumModal.cantitate)} {consumModal.um}
                </p>
                <div className="field-row">
                  <div className="field field-compact" style={{ flex: '0 0 88px' }}>
                    <label>Cantitate *</label>
                    <Combobox
                      options={consumModal.cantitate > 0
                        ? Array.from({ length: Math.floor(consumModal.cantitate) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
                        : []}
                      value={cantitateConsum} placeholder="1-50" allowFree
                      onTextChange={(txt) => setCantitateConsum(filtreazaCantitate(txt))}
                      onSelect={(val) => setCantitateConsum(String(val))} />
                  </div>
                  <div className="field field-compact" style={{ flex: 1 }}>
                    <label>Motiv *</label>
                    <select value={motivConsum}
                      onChange={e => { setMotivConsum(e.target.value); setProdusFinitTintaId(''); }} required>
                      <option value="">Alege...</option>
                      <option value="PRODUCTIE">Transfer catre productie (consum)</option>
                      <option value="CASARE">Casare</option>
                      <option value="PIERDERE">Pierdere</option>
                      <option value="DISTRUGERE">Distrugere</option>
                    </select>
                  </div>
                </div>
                <div className="field-row">
                  <div className="field field-compact">
                    <label>Document</label>
                    <input value={documentConsum} onChange={e => setDocumentConsum(e.target.value)} />
                  </div>
                  <div className="field field-compact">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={dataDocumentConsum} onChange={e => setDataDocumentConsum(e.target.value)} required />
                  </div>
                </div>
                {motivConsum === 'PRODUCTIE' && (
                  <div className="field">
                    <label>Produs finit in productie (pentru cost) *</label>
                    <Combobox
                      options={produseInProductie.map(p => ({ value: p.id, label: p.cod ? `${p.cod} - ${p.denumire}` : p.denumire }))}
                      value={produsFinitTintaId} placeholder="Alege produsul..."
                      onSelect={(val) => { setProdusFinitTintaId(val || ''); setLotProductieId(''); }} />
                    {produseInProductie.length === 0 && (
                      <span style={{ fontSize: 12, color: '#c0392b' }}>Niciun produs in productie momentan.</span>
                    )}
                  </div>
                )}
                {motivConsum === 'PRODUCTIE' && produsFinitTintaId && (
                  <div className="field">
                    <label>Lot (bucata) *</label>
                    <select value={lotProductieId} onChange={e => setLotProductieId(e.target.value)} required>
                      <option value="">Alege...</option>
                      {loturiActiveProdus.map(lot => (
                        <option key={lot.id} value={lot.id}>
                          {lot.client} - {lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : ''}
                        </option>
                      ))}
                    </select>
                    {loturiActiveProdus.length === 0 && (
                      <span style={{ fontSize: 12, color: '#c0392b' }}>Niciun lot activ - porneste unul din Comenzi.</span>
                    )}
                  </div>
                )}
                {/* Spatiu rezervat ca dropdown-ul de produse (Combobox, max 200px) sa
                    aiba loc sa se deschida complet, fara sa fie taiat de marginea
                    modalului - scroll-ul ramane DOAR pe dropdown, nu pe tot modalul. */}
                <div style={{ height: 96 }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setConsumModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Confirma consumul'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
